"use client"

import { useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Progress } from "@/components/ui/progress"
import BusinessService from "@/api/businesses.service";
import { Input } from "@/components/ui/input"
import { SendIcon, Loader2, ArrowLeft } from "lucide-react"
import {useToast} from "@/hooks/use-toast";
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'

const formSchema = z.object({
  businessType: z.string(),
  name: z.string(),
  description: z.string(),
  address: z.string(),
  city: z.string(),
  state: z.string().optional(), // Added state field
  postalCode: z.string(), // Added postalCode field
  country: z.string(),
  emailAddress: z.string().email(),
  phoneNumber: z.string(),
  website: z.string(),
  logoImgLink: z.string(),
  bannerImgLink: z.string(),
})

export type Business = {
  id: number
  businessType: string
  name: string
  description: string
  address: string
  city: string
  state?: string // Added state field
  postalCode: string // Added postalCode field
  country: string
  emailAddress: string
  phoneNumber: string
  website: string
  logoImgLink: string
  bannerImgLink: string
  dateCreated: string
}

export default function CreateBusinessForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [logoImg, setLogoImg] = useState(null);
  const [bannerImg, setBannerImg] = useState(null);
  
  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      businessType: "",
      name: "",
      description: "",
      address: "",
      city: "",
      state: "", // Added state field
      postalCode: "", // Added postalCode field
      country: "",
      emailAddress: "",
      phoneNumber: "",
      website: "",
      logoImgLink: "",
      bannerImgLink: ""
    },
  })

 const { toast } = useToast();
  
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      formData.append(key, value);
    });
    if (logoImg) {
      formData.append('logoImg', logoImg);
    }
    if (bannerImg) {
      formData.append('bannerImg', bannerImg);
    }
    setIsSubmitting(true);
    try {
      const response = await BusinessService.addBusiness(formData, progressEvent => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setProgress(percentCompleted);
      });
      const result = response.data;
      if (result.result === "success") {
        toast({
          title: "Success",
          description: "Business added successfully",
          duration: 3000,
        });

        // Append the new business to local storage
        const storedBusinesses = localStorage.getItem('businesses');
        const businesses = storedBusinesses ? JSON.parse(storedBusinesses) : [];
        businesses.push(result.business);
        localStorage.setItem('businesses', JSON.stringify(businesses));

        // Select the new business
        localStorage.setItem('selectedBusiness', JSON.stringify(result.business));

        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      } else if (result.result === "notavailable") {
        toast({
          title: "Error",
          description: result.message || "Failed to add business",
          duration: 3000,
        });
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to add business",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to add business",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleUpload = (imageType: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      if (imageType === "logoImg") {
        setLogoImg(e.target.files[0] as any);
      } else if (imageType === "bannerImg") {
        setBannerImg(e.target.files[0] as any);
      } else {
        toast({
          title: "Error",
        description: "Please select a valid image",
          duration: 3000,
        });
        return;
      }
    }
  }

  return (
      <div className="container py-10 mx-auto">
        <h2 className="text-3xl font-bold tracking-tight">Add New Business</h2>
        <div className='flex items-start align-middle mb-5'>
          <Button onClick={() => window.history.back()} className='mr-2'> 
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Back</Button>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="grid sm:grid-cols-2 grid-cols-1 gap-4">

              <FormField
                control={form.control}
                name="businessType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Type <span className="text-red-700">*</span></FormLabel>
                    <FormControl>
                      <Select onValueChange={(value) => field.onChange(value)} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select business type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent style={{ maxHeight: '200px', overflowY: 'scroll' }}>
                          <SelectItem value="sole_proprietorship">Sole Proprietorship</SelectItem>
                          <SelectItem value="partnership">Partnership</SelectItem>
                          <SelectItem value="corporation">Corporation</SelectItem>
                          <SelectItem value="limited_liability_company">Private Limited Company</SelectItem>
                          <SelectItem value="nonprofit">Non-Profit Organization</SelectItem>
                          <SelectItem value="ngo">Non-Governmental Organization</SelectItem>
                          <SelectItem value="trust">Trust</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormDescription>
                      Select the business type.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name <span className="text-red-700">*</span></FormLabel>
                    <FormControl>
                      <Input placeholder="Business Name" {...field} />
                    </FormControl>
                    <FormDescription>
                      Enter the business name.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description <span className="text-red-700">*</span></FormLabel>
                    <FormControl>
                      <Input placeholder="Description" {...field} />
                    </FormControl>
                    <FormDescription>
                      Enter the business description.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address <span className="text-red-700">*</span></FormLabel>
                    <FormControl>
                      <Input placeholder="123 Main St" {...field} />
                    </FormControl>
                    <FormDescription>
                      Enter the business address.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State</FormLabel>
                    <FormControl>
                      <Input placeholder="State" {...field} />
                    </FormControl>
                    <FormDescription> Enter the business state. </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City <span className="text-red-700">*</span></FormLabel>
                    <FormControl>
                      <Input placeholder="New York" {...field} />
                    </FormControl>
                    <FormDescription>
                      Enter the business city.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Postal Code <span className="text-red-700">*</span></FormLabel>
                    <FormControl>
                      <Input placeholder="12345" {...field} />
                    </FormControl>
                    <FormDescription>
                      Enter the business postal code.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country <span className="text-red-700">*</span></FormLabel>
                    <FormControl>
                      <Select {...field} defaultValue={field.value ?? ''} value={field.value ?? ''} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a country" />
                        </SelectTrigger>
                        <SelectContent style={{ maxHeight: '200px', overflowY: 'scroll' }}>
                          <SelectItem value="Zimbabwe">Zimbabwe</SelectItem>
                          <SelectItem value="South Africa">South Africa</SelectItem>
                          <SelectItem value="Algeria">Algeria</SelectItem>
                          <SelectItem value="Angola">Angola</SelectItem>
                          <SelectItem value="Benin">Benin</SelectItem>
                          <SelectItem value="Botswana">Botswana</SelectItem>
                          <SelectItem value="Burkina Faso">Burkina Faso</SelectItem>
                          <SelectItem value="Burundi">Burundi</SelectItem>
                          <SelectItem value="Cabo Verde">Cabo Verde</SelectItem>
                          <SelectItem value="Cameroon">Cameroon</SelectItem>
                          <SelectItem value="Central African Republic">Central African Republic</SelectItem>
                          <SelectItem value="Chad">Chad</SelectItem>
                          <SelectItem value="Comoros">Comoros</SelectItem>
                          <SelectItem value="Congo">Congo</SelectItem>
                          <SelectItem value="Djibouti">Djibouti</SelectItem>
                          <SelectItem value="Egypt">Egypt</SelectItem>
                          <SelectItem value="Equatorial Guinea">Equatorial Guinea</SelectItem>
                          <SelectItem value="Eritrea">Eritrea</SelectItem>
                          <SelectItem value="Eswatini">Eswatini</SelectItem>
                          <SelectItem value="Ethiopia">Ethiopia</SelectItem>
                          <SelectItem value="Gabon">Gabon</SelectItem>
                          <SelectItem value="Gambia">Gambia</SelectItem>
                          <SelectItem value="Ghana">Ghana</SelectItem>
                          <SelectItem value="Guinea">Guinea</SelectItem>
                          <SelectItem value="Guinea-Bissau">Guinea-Bissau</SelectItem>
                          <SelectItem value="Ivory Coast">Ivory Coast</SelectItem>
                          <SelectItem value="Kenya">Kenya</SelectItem>
                          <SelectItem value="Lesotho">Lesotho</SelectItem>
                          <SelectItem value="Liberia">Liberia</SelectItem>
                          <SelectItem value="Libya">Libya</SelectItem>
                          <SelectItem value="Madagascar">Madagascar</SelectItem>
                          <SelectItem value="Malawi">Malawi</SelectItem>
                          <SelectItem value="Mali">Mali</SelectItem>
                          <SelectItem value="Mauritania">Mauritania</SelectItem>
                          <SelectItem value="Mauritius">Mauritius</SelectItem>
                          <SelectItem value="Morocco">Morocco</SelectItem>
                          <SelectItem value="Mozambique">Mozambique</SelectItem>
                          <SelectItem value="Namibia">Namibia</SelectItem>
                          <SelectItem value="Niger">Niger</SelectItem>
                          <SelectItem value="Nigeria">Nigeria</SelectItem>
                          <SelectItem value="Rwanda">Rwanda</SelectItem>
                          <SelectItem value="Sao Tome and Principe">Sao Tome and Principe</SelectItem>
                          <SelectItem value="Senegal">Senegal</SelectItem>
                          <SelectItem value="Seychelles">Seychelles</SelectItem>
                          <SelectItem value="Sierra Leone">Sierra Leone</SelectItem>
                          <SelectItem value="Somalia">Somalia</SelectItem>
                          <SelectItem value="South Sudan">South Sudan</SelectItem>
                          <SelectItem value="Sudan">Sudan</SelectItem>
                          <SelectItem value="Tanzania">Tanzania</SelectItem>
                          <SelectItem value="Togo">Togo</SelectItem>
                          <SelectItem value="Tunisia">Tunisia</SelectItem>
                          <SelectItem value="Uganda">Uganda</SelectItem>
                          <SelectItem value="Zambia">Zambia</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormDescription>
                      Enter the business country.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emailAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address <span className="text-red-700">*</span></FormLabel>
                    <FormControl>
                      <Input placeholder="business@example.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      Enter the business email address.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number <span className="text-red-700">*</span></FormLabel>
                    <FormControl>
                      <PhoneInput placeholder="+1234567890" {...field} className="appearance-none px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm" />
                    </FormControl>
                    <FormDescription>
                      Enter the business phone number.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website</FormLabel>
                    <FormControl>
                      <Input placeholder="https://example.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      Enter the business website.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

            </div>
            <div className="grid sm:grid-cols-2 grid-cols-1 gap-4">
              <FormField
                control={form.control}
                name="logoImgLink"
                render={({ }) => (
                  <FormItem>
                    <FormLabel>Logo Image</FormLabel>
                    <FormControl>
                      <Input type="file" onChange={(e) => handleUpload("logoImg", e)} />
                    </FormControl>
                    <FormDescription>
                      Upload the business logo image.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bannerImgLink"
                render={({ }) => (
                  <FormItem>
                    <FormLabel>Banner Image</FormLabel>
                    <FormControl>
                      <Input type="file" onChange={(e) => handleUpload("bannerImg", e)} />
                    </FormControl>
                    <FormDescription>
                      Upload the business banner image.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

            </div>
            { isSubmitting && <Progress value={progress} style={{ backgroundColor: "#0E415E", color: "#0E415E" }} /> }
            <div className="flex justify-end">
              <Button type="submit" className="bg-[#0E415E] text-white">
                {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating...</> : <><SendIcon className="w-4 h-4 mr-2" /> Create Business</>}
              </Button>
            </div>
          </form>
        </Form>
      </div>

  )
}
